import React from 'react';
import { HelpBox } from 'components/v2/help-boxes';
import { FieldPanelFullWidth, FieldPanelHeader } from 'styles/v2/Styles.styled';
import { CompilerCompositeStatusModel } from 'types/types';
import { ActionState } from 'services/EpdClient';
import { FindButtonBox } from 'components/v2/form/styled';
import { Button } from 'primereact/button';
import { useGenerateRegistrationCertificate } from 'services/api/mutations';
import { toaster } from 'components/v2/toast';
import { HyperLink } from 'styles/Styles.styled';
import Tooltip from 'components/controls/Tooltip';

const GenerateRegistrationCertificatePanel: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  validationState: CompilerCompositeStatusModel;
}> = ({ epdId, epdVersionId, validationState }) => {
  const generateRegistrationCertificateMutation = useGenerateRegistrationCertificate(epdId, epdVersionId);
  const isGenerateButtonDisabled =
    generateRegistrationCertificateMutation.isPending ||
    validationState.actionsStatus.generateRegistrationCertificate === ActionState.Disabled;

  const handleGenerateRegistrationCertificate = async () => {
    try {
      if (!window.navigator.onLine) {
        throw new Error('Cannot generate registration certificate while offline.');
      }

      await generateRegistrationCertificateMutation.mutateAsync();
      toaster({
        severity: 'success',
        summary: 'Registration certificate has been generated successfully!',
        details: (
          <>
            It is now available on the EPD details page at&nbsp;
            <HyperLink href="https://www.environdec.com/library" target="_blank" rel="noopener noreferrer">
              environdec.com
            </HyperLink>
            .
          </>
        ),
      });
    } catch (error: any) {
      toaster({
        severity: 'error',
        summary: 'Something went wrong while generating your certificate.',
        details: 'Please try again or contact support if the problem continues.',
      });
    }
  };

  return (
    <FieldPanelFullWidth>
      <FieldPanelHeader>Registration Certificate</FieldPanelHeader>
      <div>
        <HelpBox>
          <span>
            You can automatically generate a registration certificate for the <b>currently published EPD version</b>. An EPD
            update or new version is <b>not</b> required for this.
          </span>
          <span>
            The registration certificate will be accessible on the EPD details page at&nbsp;
            <HyperLink href="https://www.environdec.com/library" target="_blank" rel="noopener noreferrer">
              environdec.com
            </HyperLink>
            .
          </span>
        </HelpBox>
        {validationState.actionsStatus.generateRegistrationCertificate !== ActionState.Hidden && (
          <FindButtonBox style={{ marginTop: '10px', width: 'fit-content' }}>
            <Button
              style={{ fontSize: 14, fontWeight: 500 }}
              type="button"
              label={
                generateRegistrationCertificateMutation.isPending
                  ? 'Generating registration certificate...'
                  : '+ Generate registration certificate'
              }
              text
              onClick={handleGenerateRegistrationCertificate}
              disabled={isGenerateButtonDisabled}
            />
            {validationState.actionsStatus.generateRegistrationCertificate === ActionState.Disabled &&
              !generateRegistrationCertificateMutation.isPending && (
                <Tooltip position="bottom" offset={5}>
                  A certificate already exists for the currently published EPD version. Another one can be generated only
                  after publishing a new EPD version.
                </Tooltip>
              )}
          </FindButtonBox>
        )}
      </div>
    </FieldPanelFullWidth>
  );
};

export default GenerateRegistrationCertificatePanel;
